import React, { useState, useCallback, useEffect } from 'react';
import TopBar from './navigation/TopBar';
import Sidebar from './navigation/Sidebar';
import { ErrorBoundary } from './ErrorBoundary';
import styles from '@/styles/components/Layout.module.css';

const MOBILE_BREAKPOINT = 768;

/**
 * Layout component props interface
 * Extends HTML div attributes for full compatibility
 */
export interface LayoutProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Content to render in the main area */
  children: React.ReactNode;
  /** Whether to show the top bar border */
  showTopBarBorder?: boolean;
  /** Initial sidebar visibility state */
  initialSidebarVisible?: boolean;
  /** Custom styles for the main content area */
  contentStyle?: React.CSSProperties;
  /** Additional CSS class names */
  className?: string;
  /** Custom inline styles */
  style?: React.CSSProperties;
}

/**
 * Layout component following design system standards
 * Provides the main application layout with sidebar and top bar
 * 
 * @example
 * ```tsx
 * <Layout showTopBarBorder initialSidebarVisible={true}>
 *   <div>Main content goes here</div>
 * </Layout>
 * ```
 */
const Layout: React.FC<LayoutProps> = ({
  children,
  showTopBarBorder = false,
  initialSidebarVisible = true,
  contentStyle,
  className,
  style,
  ...props
}) => {
  const [isSidebarVisible, setIsSidebarVisible] = useState(initialSidebarVisible);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches : false
  );
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const handleChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handleChange);
    return () => mq.removeEventListener('change', handleChange);
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsSidebarVisible(prev => !prev);
  }, []);

  const openMobileMenu = useCallback(() => setIsMobileMenuOpen(true), []);
  const closeMobileMenu = useCallback(() => setIsMobileMenuOpen(false), []);

  const layoutClassName = `${styles.layout} ${className || ''}`.trim();
  const topBarWrapperClassName = `${styles.topBarWrapper} ${
    isSidebarVisible ? styles.sidebarVisible : styles.sidebarHidden
  }`;
  const mainContentClassName = `${styles.mainContent} ${
    isSidebarVisible ? styles.sidebarVisible : styles.sidebarHidden
  }`;

  return (
    <div
      style={style}
      className={layoutClassName}
      {...props}
    >
      <ErrorBoundary>
        <Sidebar
          isVisible={true}
          isCollapsed={!isSidebarVisible}
          onToggle={toggleSidebar}
          isMobile={isMobile}
          isMobileMenuOpen={isMobileMenuOpen}
          onCloseMobileMenu={closeMobileMenu}
        />
      </ErrorBoundary>

      <div className={topBarWrapperClassName}>
        <ErrorBoundary>
          <TopBar
            showBorder={showTopBarBorder}
            isMobile={isMobile}
            onOpenMobileMenu={openMobileMenu}
          />
        </ErrorBoundary>
      </div>

      {/* Main content area */}
      <main className={mainContentClassName} style={contentStyle}>
        <ErrorBoundary>
          {React.Children.map(children, child => {
            if (React.isValidElement(child)) {
              return React.cloneElement(child, { 
                isSidebarVisible,
                ...child.props 
              });
            }
            return child;
          })}
        </ErrorBoundary>
      </main>
    </div>
  );
};

Layout.displayName = 'Layout';

export default Layout; 